import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Trash2, FolderInput, AlertTriangle, Copy, Layers, GitMerge, Scissors } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { KbCategoryFilter } from "@/components/KbCategoryFilter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import AppUnavailable from "@/pages/AppUnavailable";

const flatten = (nodes, depth = 0, out = []) => {
  for (const n of nodes || []) {
    out.push({ slug: n.slug, cat_id: n.cat_id, name: `${"— ".repeat(depth)}${(n.name || "").trim()}` });
    flatten(n.children, depth + 1, out);
  }
  return out;
};

const TABS = [
  { key: "empty", label: "Empty categories", icon: Layers },
  { key: "orphans", label: "Orphan articles", icon: FolderInput },
  { key: "overloaded", label: "Overloaded", icon: AlertTriangle },
  { key: "duplicates", label: "Duplicate names", icon: Copy },
];

export default function KBCategoryHealth() {
  const navigate = useNavigate();
  const [denied, setDenied] = useState(false);
  const [data, setData] = useState(null);
  const [tree, setTree] = useState([]);
  const [flatCats, setFlatCats] = useState([]);
  const [tab, setTab] = useState("empty");
  const [assign, setAssign] = useState({}); // article_id -> cat_id
  const [mergeTo, setMergeTo] = useState({}); // source_slug -> target_slug
  const [merging, setMerging] = useState("");
  // split dialog
  const [split, setSplit] = useState(null); // {cat_id, name, slug}
  const [splitArts, setSplitArts] = useState(null);
  const [splitSel, setSplitSel] = useState(new Set());
  const [splitTarget, setSplitTarget] = useState("");
  const [newSub, setNewSub] = useState("");
  const [moving, setMoving] = useState(false);

  const load = useCallback(() => {
    api.get("/kb/category-health").then((r) => setData(r.data))
      .catch((e) => { if (e.response?.status === 403) setDenied(true); });
  }, []);

  useEffect(() => {
    load();
    api.get("/kb/tree").then((r) => { setTree(r.data || []); setFlatCats(flatten(r.data || [])); }).catch(() => {});
  }, [load]);

  const mergeCat = async (source_slug) => {
    const target_slug = mergeTo[source_slug];
    if (!target_slug) { toast.error("Pick a category to merge into"); return; }
    if (!window.confirm("Merge this category's articles and sub-categories into the selected one, then remove it?")) return;
    setMerging(source_slug);
    try {
      const { data: r } = await api.post("/kb/categories/merge", { source_slug, target_slug });
      toast.success(`Merged into ${r.target} (${r.moved} articles)`);
      api.get("/kb/tree").then((res) => { setTree(res.data || []); setFlatCats(flatten(res.data || [])); });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setMerging(""); }
  };

  const openSplit = async (cat) => {
    setSplit(cat); setSplitArts(null); setSplitSel(new Set()); setSplitTarget(""); setNewSub("");
    try {
      const { data: r } = await api.get("/kb/manage/articles", { params: { category_id: cat.cat_id, limit: 200 } });
      setSplitArts(r.articles || []);
    } catch { setSplitArts([]); }
  };

  const doSplit = async () => {
    if (splitSel.size === 0) { toast.error("Select articles to move"); return; }
    if (!splitTarget && !newSub.trim()) { toast.error("Choose or name a target category"); return; }
    setMoving(true);
    try {
      let targetCatId = splitTarget;
      if (newSub.trim()) {
        const { data: c } = await api.post("/kb/categories", { name: newSub.trim(), parent_id: split.cat_id });
        targetCatId = (c.cat || c).cat_id;
      }
      // article_ids in manage list are slugs; move needs article_ids — look them up
      await api.post("/kb/articles/move", {
        article_ids: [...splitSel], from_cat_id: split.cat_id, target_cat_id: targetCatId });
      toast.success(`Moved ${splitSel.size} article(s)`);
      setSplit(null);
      api.get("/kb/tree").then((res) => { setTree(res.data || []); setFlatCats(flatten(res.data || [])); });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setMoving(false); }
  };

  const deleteCat = async (slug, name) => {
    if (!window.confirm(`Delete empty category “${name}”?`)) return;
    try {
      await api.delete(`/kb/categories/${slug}`);
      toast.success("Category deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const assignOrphan = async (article_id, name) => {
    const target = assign[article_id];
    if (!target || target === "all") { toast.error("Pick a category first"); return; }
    try {
      await api.post("/kb/articles/move", { article_ids: [article_id], target_cat_id: target });
      toast.success(`“${name}” filed`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (denied) return <AppUnavailable appName="KB" inactive={false} />;

  return (
    <div className="mx-auto max-w-4xl" data-testid="kb-category-health-page">
      <button onClick={() => navigate("/kb")} data-testid="kb-health-back"
        className="mb-4 flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100">
        <ArrowLeft className="h-4 w-4" /> Back to Knowledge Base
      </button>
      <h1 className="mb-4 font-heading text-2xl font-bold text-slate-900 dark:text-slate-100">Category Health</h1>

      {!data ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300 dark:text-slate-600" /></div>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap gap-2" data-testid="kb-health-tabs">
            {TABS.map((t) => {
              const n = data.counts[t.key === "duplicates" ? "duplicate_names" : t.key] ?? 0;
              const Icon = t.icon;
              return (
                <button key={t.key} onClick={() => setTab(t.key)} data-testid={`kb-health-tab-${t.key}`}
                  className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium ${tab === t.key ? "border-primary bg-primary/10 text-primary" : "border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
                  <Icon className="h-3.5 w-3.5" /> {t.label}
                  <span className="rounded-full bg-slate-100 dark:bg-slate-800 px-1.5 text-[10px] text-slate-600 dark:text-slate-300">{n}</span>
                </button>
              );
            })}
          </div>

          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
            {tab === "empty" && (
              <div className="space-y-1.5" data-testid="kb-health-empty">
                {data.empty.length === 0 && <p className="text-sm text-slate-400 dark:text-slate-500">No empty categories. 🎉</p>}
                {data.empty.map((c) => (
                  <div key={c.cat_id} className="flex items-center justify-between rounded-lg border border-slate-100 dark:border-slate-800 px-3 py-2">
                    <span className="text-sm text-slate-700 dark:text-slate-200">{c.name}{c.has_children && <span className="ml-2 text-xs text-slate-400 dark:text-slate-500">(has sub-categories)</span>}</span>
                    <Button size="sm" variant="outline" data-testid={`kb-health-delete-${c.slug}`}
                      disabled={c.has_children} onClick={() => deleteCat(c.slug, c.name)}
                      className="border-rose-200 text-rose-600 hover:bg-rose-50">
                      <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {tab === "orphans" && (
              <div className="space-y-1.5" data-testid="kb-health-orphans">
                {data.orphans.length === 0 && <p className="text-sm text-slate-400 dark:text-slate-500">No orphan articles. 🎉</p>}
                {data.orphans.map((o) => (
                  <div key={o.article_id} className="flex flex-col gap-2 rounded-lg border border-slate-100 dark:border-slate-800 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                    <span className="min-w-0 truncate text-sm text-slate-700 dark:text-slate-200">
                      {o.name}{o.is_draft && <span className="ml-2 rounded bg-amber-100 px-1.5 text-[10px] text-amber-700">Draft</span>}
                    </span>
                    <div className="flex items-center gap-2">
                      <div className="w-56">
                        <KbCategoryFilter tree={tree} value={assign[o.article_id] || "all"} single
                          allLabel="Pick a category" testid={`kb-health-cat-${o.slug}`}
                          onChange={(id) => setAssign((a) => ({ ...a, [o.article_id]: id }))} />
                      </div>
                      <Button size="sm" data-testid={`kb-health-assign-${o.slug}`}
                        onClick={() => assignOrphan(o.article_id, o.name)}
                        className="bg-primary text-primary-foreground hover:bg-primary/90">File</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === "overloaded" && (
              <div className="space-y-1.5" data-testid="kb-health-overloaded">
                <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">Categories with {data.overloaded_threshold}+ direct articles — split some off or merge duplicates.</p>
                {data.overloaded.length === 0 && <p className="text-sm text-slate-400 dark:text-slate-500">Nothing overloaded.</p>}
                {data.overloaded.map((c) => (
                  <div key={c.cat_id} className="flex flex-col gap-2 rounded-lg border border-slate-100 dark:border-slate-800 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                    <button className="text-sm text-slate-700 dark:text-slate-200 hover:text-primary" onClick={() => navigate(`/kb/c/${c.slug}`)}>{c.name}</button>
                    <div className="flex items-center gap-2">
                      <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">{c.count}</span>
                      <Button size="sm" variant="outline" data-testid={`kb-health-split-${c.slug}`} onClick={() => openSplit(c)}>
                        <Scissors className="mr-1 h-3.5 w-3.5" /> Split
                      </Button>
                      <select data-testid={`kb-health-mergeto-${c.slug}`} value={mergeTo[c.slug] || ""}
                        onChange={(e) => setMergeTo((m) => ({ ...m, [c.slug]: e.target.value }))}
                        className="max-w-[160px] rounded-md border border-slate-200 dark:border-slate-800 px-2 py-1 text-xs text-slate-600 dark:text-slate-300">
                        <option value="">Merge into…</option>
                        {flatCats.filter((f) => f.slug !== c.slug).map((f) => <option key={f.slug} value={f.slug}>{f.name}</option>)}
                      </select>
                      <Button size="sm" data-testid={`kb-health-merge-${c.slug}`} disabled={merging === c.slug}
                        onClick={() => mergeCat(c.slug)} className="bg-primary text-primary-foreground hover:bg-primary/90">
                        <GitMerge className="mr-1 h-3.5 w-3.5" /> Merge
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === "duplicates" && (
              <div className="space-y-1.5" data-testid="kb-health-duplicates">
                {data.duplicates.length === 0 && <p className="text-sm text-slate-400 dark:text-slate-500">No duplicate category names.</p>}
                {data.duplicates.map((d, i) => (
                  <div key={i} className="rounded-lg border border-slate-100 dark:border-slate-800 px-3 py-2">
                    <div className="text-sm font-medium text-slate-700 dark:text-slate-200">{d.name}</div>
                    <div className="mt-1 space-y-1">
                      {d.slugs.map((s) => (
                        <div key={s} className="flex items-center justify-between gap-2">
                          <button onClick={() => navigate(`/kb/c/${s}`)}
                            className="rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-xs text-slate-600 dark:text-slate-300 hover:bg-slate-200">{s}</button>
                          <div className="flex items-center gap-1.5">
                            <select data-testid={`kb-health-dup-mergeto-${s}`} value={mergeTo[s] || ""}
                              onChange={(e) => setMergeTo((m) => ({ ...m, [s]: e.target.value }))}
                              className="max-w-[150px] rounded-md border border-slate-200 dark:border-slate-800 px-2 py-1 text-xs text-slate-600 dark:text-slate-300">
                              <option value="">Merge into…</option>
                              {flatCats.filter((f) => f.slug !== s).map((f) => <option key={f.slug} value={f.slug}>{f.name}</option>)}
                            </select>
                            <Button size="sm" variant="outline" data-testid={`kb-health-dup-merge-${s}`}
                              disabled={merging === s} onClick={() => mergeCat(s)}>
                              <GitMerge className="mr-1 h-3.5 w-3.5" /> Merge
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {split && (
        <Dialog open onOpenChange={(v) => { if (!v) setSplit(null); }}>
          <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col gap-0 p-0" data-testid="kb-split-dialog">
            <DialogHeader className="border-b border-border px-6 py-4">
              <DialogTitle>Split “{split.name}”</DialogTitle>
            </DialogHeader>
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-6 py-4">
              <p className="text-xs text-slate-500 dark:text-slate-400">Select articles to move out, then choose an existing target or create a new sub-category.</p>
              <div className="space-y-1.5 rounded-lg border border-slate-200 dark:border-slate-800 p-2">
                <div className="mb-1 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                  <button className="font-medium text-primary hover:underline" data-testid="kb-split-select-all"
                    onClick={() => setSplitSel((s) => s.size === (splitArts || []).length ? new Set() : new Set((splitArts || []).map((a) => a.article_id)))}>
                    {splitArts && splitSel.size === splitArts.length && splitArts.length > 0 ? "Clear" : "Select all"}
                  </button>
                  <span>{splitSel.size} selected</span>
                </div>
                <div className="max-h-64 space-y-0.5 overflow-y-auto" data-testid="kb-split-list">
                  {!splitArts ? <div className="flex h-20 items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-slate-300 dark:text-slate-600" /></div>
                    : splitArts.length === 0 ? <p className="py-4 text-center text-sm text-slate-400 dark:text-slate-500">No articles.</p>
                    : splitArts.map((a) => (
                      <label key={a.article_id} data-testid={`kb-split-row-${a.slug}`}
                        className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-slate-50 dark:hover:bg-slate-800">
                        <input type="checkbox" checked={splitSel.has(a.article_id)}
                          onChange={() => setSplitSel((s) => { const n = new Set(s); n.has(a.article_id) ? n.delete(a.article_id) : n.add(a.article_id); return n; })} />
                        <span className="min-w-0 truncate text-slate-700 dark:text-slate-200">{a.name}</span>
                      </label>
                    ))}
                </div>
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Move to existing category</label>
                  <select data-testid="kb-split-target" value={splitTarget} disabled={!!newSub.trim()}
                    onChange={(e) => setSplitTarget(e.target.value)}
                    className="w-full rounded-md border border-slate-200 dark:border-slate-800 px-2 py-1.5 text-xs text-slate-600 dark:text-slate-300 disabled:opacity-50">
                    <option value="">Select…</option>
                    {flatCats.filter((f) => f.slug !== split.slug).map((f) => <option key={f.slug} value={f.cat_id}>{f.name}</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-slate-600 dark:text-slate-300">…or new sub-category</label>
                  <Input data-testid="kb-split-newsub" value={newSub} placeholder="New sub-category name"
                    onChange={(e) => setNewSub(e.target.value)} className="h-8 text-xs" />
                </div>
              </div>
            </div>
            <DialogFooter className="border-t border-border px-6 py-4">
              <Button variant="outline" onClick={() => setSplit(null)}>Cancel</Button>
              <Button data-testid="kb-split-move" onClick={doSplit} disabled={moving}
                className="bg-primary text-primary-foreground hover:bg-primary/90">
                {moving ? "Moving…" : `Move ${splitSel.size} article(s)`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
